import React, { forwardRef, ReactNode } from 'react';
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from 'primereact/inputnumber';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import classNames from 'classnames';
import styles from './InputNumberField.module.scss';

interface InputNumberFieldProps {
  id: string;
  label?: string;
  name: string;
  value: number | null | undefined;
  placeholder?: string;
  onChange: (e: InputNumberValueChangeEvent) => void;
  error?: string;
  isCurrency?: boolean;
  disabled?: boolean;
  allowOnlyWholeNumbers?: boolean;
  minNumber?: number;
  maxNumber?: number;
  classnames?: string;
  children?: ReactNode;
  thousandSeparators?: boolean;
  onFocus?: () => void;
  maxLength?: number;
  required?: boolean;
  iconError?: IconProp;
}

const InputNumberField = forwardRef<InputNumber, InputNumberFieldProps>(
  (
    {
      id,
      label,
      name,
      value,
      placeholder,
      onChange,
      error,
      isCurrency,
      disabled,
      allowOnlyWholeNumbers = false,
      minNumber,
      maxNumber,
      classnames,
      children,
      thousandSeparators = false,
      onFocus,
      maxLength,
      required,
      iconError,
    },
    ref
  ) => {
    const wholeNumber = allowOnlyWholeNumbers ? 0 : undefined;

    return (
      <div className={classNames(styles.fieldContainer, classnames)}>
        {label && (
          <label
            htmlFor={id}
            className={classNames(
              `${styles.label}`,
              error && styles.errorLabel
            )}
          >
            {label} {required && '*'}
          </label>
        )}
        {isCurrency ? (
          <InputNumber
            id={id}
            name={name}
            value={value}
            onValueChange={onChange}
            className={classNames(styles.field, error && styles.invalid)}
            placeholder={placeholder}
            inputId={isCurrency ? 'currency-us' : 'withoutgrouping'}
            useGrouping={false}
            disabled={disabled}
            mode="currency"
            maxFractionDigits={2}
            currency="USD"
            max={maxNumber}
            locale="es-ES"
            onFocus={onFocus}
            maxLength={maxLength}
            ref={ref}
          >
            {children}
          </InputNumber>
        ) : (
          <InputNumber
            id={id}
            name={name}
            value={value ?? null}
            onValueChange={onChange}
            className={classNames(styles.field, error && styles.invalid)}
            placeholder={placeholder}
            inputId="withoutgrouping"
            useGrouping={thousandSeparators}
            disabled={disabled}
            min={minNumber ?? wholeNumber ?? 0}
            mode="decimal"
            maxFractionDigits={allowOnlyWholeNumbers ? 0 : 2}
            max={maxNumber}
            locale="es-ES"
            onFocus={onFocus}
            maxLength={maxLength}
            ref={ref}
          >
            {children}
          </InputNumber>
        )}
        {error ? (
          <p className={styles.errorMessage}>
            {iconError && <FontAwesomeIcon icon={iconError} />} {error}
          </p>
        ) : null}
      </div>
    );
  }
);

InputNumberField.displayName = 'InputNumberField';

export default InputNumberField;
